import React, {
  useState,
  useRef,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from "react";
import HealthPlanSelect from "../../components/common/HealthPlanSelect";
import QuickFilters from "./components/Filters/QuickFilter";
import WorkspaceController from "../../controller/WorkspaceController";
import {
  PDC_CAB,
  POLICY_CODING,
  POLICY_CONFIGURATION,
  POLICY_RELEASE,
  POLICY_TRANSLATION,
} from "../../constants/Constants";
import {
  Box,
  Link,
  Typography,
  Backdrop,
  CircularProgress,
  Stack,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  FormGroup,
  FormControlLabel,
  Switch,
} from "@mui/material";
import api from "api";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useWorkspace } from "context/WorkspaceContext";
import { UserContext } from "context";
import { useSelector, useDispatch } from "react-redux";
import { formErrorStrfromObj, hasSkill } from "utils/shared";
import StyledDataGrid from "styles/styledDataGrid";
import { reloadPolicyData, setDialog } from "slices/appSlice";
import TopPanelComponent from "./components/TopPanelComponent";
import useGridResizeHook, {
  calculateGridHeight,
} from "hooks/useGridResizeHook";
import { convertDxLimitToCantHave } from "utils/workSpaceFilter";
import CustomToolBar from "./components/CustomToolBar";
import UseCaseNotReady from "./fragments/UseCaseNotReady";
import RowAction from "./components/RowAction";
import { styled } from "@mui/material";
const COMPLETE = "COMPLETE";
const BREAKFIX = "BREAKFIX";
import { usePostOHR } from "context/PostOHRContext";

const RESTRICTED_CHILD_ACTION_TYPES = new Set([
  "BREAKFIX",
  "CAB",
  "CODECHANGE",
]);

const STATUS_ALL = "All";
const STATUS_ACTIVE = "Active";
const STATUS_LATEST_VERSION = "Latest Version";
const STATUS_NO_RELEASE = "No Release";
const STATUS_PENDING_RELEASE = "Pending Release";
const STATUS_RELEASED = "Released";

const RELEASE_TO_UAT = "Release to UAT";
const RELEASE_TO_PRODUCTION = "Release to Production";

export const STATUS_FILTER_OPTIONS = [
  STATUS_ALL,
  STATUS_ACTIVE,
  STATUS_LATEST_VERSION,
  STATUS_NO_RELEASE,
  STATUS_PENDING_RELEASE,
  STATUS_RELEASED,
];

const getNormalizedCategoryOfWork = (row) => {
  const categoryOfWorkName = row?.bulkActionWorkflowRefDTO?.categoryOfWorkName;

  return String(categoryOfWorkName || "")
    .trim()
    .toUpperCase();
};

const updateLocalStorageForFilter = (filterType, value) => {
  const filters =
    JSON.parse(sessionStorage.getItem("policyEnforcementActiveFilter")) || {};
  filters[filterType] = value;
  sessionStorage.setItem(
    "policyEnforcementActiveFilter",
    JSON.stringify(filters)
  );
};

const HeaderTitle = styled("span")(() => ({
  fontWeight: "bold",
  color: "#000000",
  whiteSpace: "nowrap",
  overflow: "visible",
}));

const wrappedCellContentSx = {
  width: "100%",
  py: 1,
  textAlign: "center",
  whiteSpace: "normal",
  wordBreak: "break-word",
  lineHeight: 1.4,
};

const releaseCellContentSx = { ...wrappedCellContentSx };

const getFirstDisplayValue = (values = []) => {
  const match = values.find(
    (value) => value !== undefined && value !== null && String(value).trim()
  );
  return match == null ? "" : String(match).trim();
};

/**
 * Normalizes consolidated API responses into a flat row array.
 */
export const normalizePolicyEnforcementRows = (data) => {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== "object") return [];
  const candidateKeys = [
    "content",
    "rows",
    "items",
    "data",
    "results",
    "policies",
  ];
  const matchingKey = candidateKeys.find((key) => Array.isArray(data[key]));
  return matchingKey ? data[matchingKey] : [];
};

/**
 * Resolves the display label for the policy type of change.
 */
export const getTypeOfChangeDisplayValue = (row) =>
  getFirstDisplayValue([
    row?.typeOfChange,
    row?.typeOfChangeName,
    row?.typeOfChangeText,
    row?.bulkActionWorkflowRefDTO?.typeOfChange,
    row?.bulkActionWorkflowRefDTO?.typeOfChangeName,
    row?.bulkActionWorkflowRefDTO?.categoryOfWorkName,
  ]);

/**
 * Resolves the quarter label including type-of-change prefix (for example, "ICD-10 Q3 25").
 */
export const getQuarterDisplayValue = (row) => {
  const quarter = getFirstDisplayValue([row?.quarter, row?.quarterDisplay]);
  const year = getFirstDisplayValue([row?.year, row?.yearDisplay]);
  const typeOfChange = getTypeOfChangeDisplayValue(row);
  const quarterLabel = [quarter, year].filter(Boolean).join(" ").trim();
  return [typeOfChange, quarterLabel].filter(Boolean).join(" ").trim();
};

/**
 * Resolves release metadata from legacy and consolidated API shapes.
 */
export const getLatestReleaseMetadata = (row) => {
  return (
    row?.latestDeploymentRelease ??
    row?.latestDeploymentReleaseDto ??
    row?.deploymentRelease ??
    row?.release ??
    row?.poeReleaseScheduleDto ??
    null
  );
};
/**
 * Formats release data for display in the Enforcement Rules grid.
 *
 * @param {string | number | object | null | undefined} release
 * @returns {string}
 */
export const getReleaseDisplayValue = (release) => {
  if (release == null) {
    return "";
  }

  if (typeof release === "string" || typeof release === "number") {
    return String(release);
  }

  if (Array.isArray(release)) {
    return release.filter(Boolean).join(", ");
  }

  const prioritizedDisplayValue = getFirstDisplayValue([
    release.latestDeploymentRelease,
    release.latestDeploymentReleaseNumber,
    release.deploymentRelease,
    release.displayValue,
  ]);

  if (prioritizedDisplayValue) {
    return prioritizedDisplayValue;
  }

  const orderedValues = [
    release.releaseNumber,
    release.releaseName,
    release.environment,
    release.name,
    release.status,
    release.pipelineStatusText,
    release.assignedUserName,
    release.assignedUser,
  ]
    .map((value) => (value == null ? "" : String(value).trim()))
    .filter(Boolean);

  return orderedValues.join(" - ");
};

/**
 * Determines whether the policyKey column should be visible for the current environment.
 *
 * @param {string | undefined | null} environment
 * @returns {boolean}
 */
export const shouldShowPolicyKeyColumn = (environment) => {
  const normalizedEnvironment = String(environment || "")
    .trim()
    .toLowerCase();

  if (!normalizedEnvironment) {
    return true;
  }

  if (
    normalizedEnvironment.includes("uat") ||
    normalizedEnvironment === "prod" ||
    normalizedEnvironment.includes("production")
  ) {
    return false;
  }

  return true;
};

/**
 * Computes the initial status filter value from URL and persisted session state.
 */
export const getInitialStatusFilter = (statusParam, parsedFilters) =>
  statusParam || parsedFilters?.status || STATUS_ACTIVE;

/**
 * Returns true when any workflow stage (coding/translation/configuration) is not complete.
 */
export const isPolicyActive = (row) => {
  const codingStatus = row?.coding?.status;
  const translationStatus = row?.translation?.status;
  const configurationStatus = row?.configuration?.status;

  return [codingStatus, translationStatus, configurationStatus].some(
    (status) =>
      String(status || "")
        .trim()
        .toLowerCase() !== "complete"
  );
};

/**
 * Compares two version strings and returns positive when `versionA` is greater.
 */
export const comparePolicyVersions = (versionA, versionB) => {
  const toNumericSegments = (value) => {
    if (value == null) return null;
    const parts = String(value).split(".");
    if (parts.length === 0) return null;

    const segments = [];
    for (const part of parts) {
      if (!/^\d+$/.test(part)) return null;
      segments.push(Number(part));
    }
    return segments;
  };

  const numericA = toNumericSegments(versionA);
  const numericB = toNumericSegments(versionB);

  if (numericA && numericB) {
    const maxLength = Math.max(numericA.length, numericB.length);
    for (let i = 0; i < maxLength; i += 1) {
      const valueA = numericA[i] ?? 0;
      const valueB = numericB[i] ?? 0;
      if (valueA !== valueB) return valueA - valueB;
    }
    return 0;
  }

  if (numericA && !numericB) return 1;
  if (!numericA && numericB) return -1;

  return String(versionA || "").localeCompare(
    String(versionB || ""),
    undefined,
    {
      numeric: true,
      sensitivity: "base",
    }
  );
};

/**
 * Builds a set of policy keys representing the highest version per policy number.
 */
export const buildLatestVersionPolicyKeySet = (rows = []) => {
  const latestByPolicyNumber = new Map();

  rows.forEach((row) => {
    const policyNumber = String(row?.policyNumber ?? "");
    if (!policyNumber || row?.policyKey == null) return;

    const current = latestByPolicyNumber.get(policyNumber);
    if (!current) {
      latestByPolicyNumber.set(policyNumber, row);
      return;
    }

    const comparison = comparePolicyVersions(row?.version, current?.version);
    if (comparison > 0) {
      latestByPolicyNumber.set(policyNumber, row);
    }
  });

  return new Set(
    Array.from(latestByPolicyNumber.values())
      .map((row) => row?.policyKey)
      .filter((policyKey) => policyKey != null)
  );
};

/**
 * Checks whether a policy row satisfies the selected status filter.
 */
/**
 * Translates the persisted quarter label into the workflow's identifier text when configured.
 */
function getQuarterIdentifier(quarter, bulkActionWorkflowRefDTO) {
  const [quarterPart, yearPart] = quarter.split(" ");
  if (!quarterPart || !yearPart) {
    throw new Error(
      `Invalid quarter format: "${quarter}". Expected format: "Qx YY"`
    );
  }

  const fullYear = `20${yearPart}`;

  if (!bulkActionWorkflowRefDTO?.quarterAlias) {
    return `${quarterPart} ${fullYear}`;
  }

  const quarterIndex = bulkActionWorkflowRefDTO.quarterAlias
    .split(",")
    .indexOf(quarterPart);

  if (quarterIndex === -1) {
    return `${quarterPart} ${fullYear}`;
  }

  const identifier =
    bulkActionWorkflowRefDTO.identifierText?.split(",")[quarterIndex];
  return identifier
    ? `${identifier} ${fullYear}`
    : `${quarterPart} ${fullYear}`;
}

export const matchesStatusFilter = (row, status, latestVersionPolicyKeySet) => {
  const releaseMetadata = getLatestReleaseMetadata(row);
  const releaseDisplayValue = getReleaseDisplayValue(releaseMetadata);
  const pipelineStatus = releaseMetadata?.pipelineStatusText;

  if (status === STATUS_ALL) return true;
  if (status === STATUS_ACTIVE) return isPolicyActive(row);
  if (status === STATUS_LATEST_VERSION) {
    return latestVersionPolicyKeySet.has(row?.policyKey);
  }
  if (status === STATUS_NO_RELEASE) {
    return !releaseDisplayValue;
  }
  if (status === STATUS_PENDING_RELEASE) {
    return (
      !!releaseDisplayValue &&
      !!pipelineStatus &&
      pipelineStatus !== RELEASE_TO_PRODUCTION
    );
  }
  if (status === STATUS_RELEASED) {
    return (
      pipelineStatus === RELEASE_TO_UAT ||
      pipelineStatus === RELEASE_TO_PRODUCTION ||
      (!!releaseDisplayValue && !pipelineStatus)
    );
  }

  return true;
};

function PolicyEnforcementRules() {
  const dispatch = useDispatch();
  const { state } = useLocation();
  const [pageType, setPageType] = useState(() => {
    if (typeof window === "undefined") return undefined;
    return localStorage.getItem("policyEnforcementPageType") || undefined;
  });
  const topPanelRef = useRef(null);
  const [gridHeight, setGridHeight] = useState("auto");

  useEffect(() => {
    if (state?.pageType == null) return undefined;
    localStorage.setItem("policyEnforcementPageType", state.pageType);
    const timeoutId = setTimeout(() => {
      setPageType((prev) => (prev === state.pageType ? prev : state.pageType));
    }, 0);
    return () => clearTimeout(timeoutId);
  }, [state]);
  const shouldReloadPolicyData = useSelector(
    (state) => state.app.reloadPolicyData
  );
  const selectedHealthPlan = useSelector(
    (state) => state.healthPlan.healthPlan
  );

  const [allData, setAllData] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const assignee = searchParams.get("assignee");
  const status = searchParams.get("status");

  const parsedFilters = JSON.parse(
    sessionStorage.getItem("policyEnforcementActiveFilter")
  );
  const [selectedStatus, setSelectedStatus] = useState(
    getInitialStatusFilter(status, parsedFilters)
  );
  const [selectedAssignee, setSelectedAssignee] = useState(
    assignee || parsedFilters?.assignee || "All"
  );
  const [isLoading, setIsLoading] = useState(false); // Single loading state
  const [selectedPolicies, setSelectedPolicies] = useState([]);
  const [showBulkUpdate, setShowBulkUpdate] = useState(false);
  const [openPolicyNotReadyDialog, setOpenPolicyNotReadyDialog] =
    useState(false);
  const { postOHR, setPostOHR } = usePostOHR();
  useEffect(() => {
    const newParam = new URLSearchParams(searchParams);
    newParam.set("status", selectedStatus);
    newParam.set("assignee", selectedAssignee);
    setSearchParams(newParam);
  }, []);

  /**
   * Updates a single filter value in both URL query params and session storage.
   */
  const updateSearchParam = useCallback(
    (filter, value) => {
      const newParam = new URLSearchParams(searchParams);
      if (newParam.get(filter)) newParam.set(filter, value);
      else newParam.append(filter, value);
      setSearchParams(newParam);

      updateLocalStorageForFilter(filter, value);
    },
    [searchParams]
  );

  /**
   * Resets the active quick filters and clears persisted URL/storage values.
   */
  const resetFilter = useCallback(() => {
    setSelectedStatus(STATUS_ACTIVE);
    setSelectedAssignee("All");
    const newParam = new URLSearchParams(searchParams);
    newParam.delete("status");
    newParam.delete("assignee");
    setSearchParams(newParam);
    sessionStorage.removeItem("policyEnforcementActiveFilter");
  }, [searchParams, setSearchParams]);

  const onRowSelectionModelChange = (newSelection) => {
    // v6 sometimes gives an object with { type, ids: Set }, sometimes an array
    const ids = Array.isArray(newSelection)
      ? newSelection
      : Array.from(newSelection?.ids ?? []); // handles empty/undefined safely

    const selectedRowsData = ids
      .map((id) => rowData.find((row) => getRowById(row) === id))
      .filter(Boolean);

    setSelectedPolicies(selectedRowsData);
    setShowBulkUpdate(
      selectedRowsData.length > 0 &&
        hasSkill(userContext, ["Manager", "Coding", "Policy"])
    );
  };

  const navigate = useNavigate();

  const { setWorkspaceController } = useWorkspace();
  const userContext = useContext(UserContext);

  const getPolicyRules = useCallback(
    async (healthPlan, props) => {
      setIsLoading(true); // Start loading
      const workspaceController = new WorkspaceController(
        props.row["policyKey"],
        healthPlan.healthPlanKey,
        props.field,
        props.row[props.field].status
      );
      try {
        await workspaceController.initialize();
      } catch (error) {
        console.error("Initialization failed. Aborting navigation.");
        setIsLoading(false);
        dispatch(
          setDialog({
            message: formErrorStrfromObj(error),
            title: "Error",
          })
        );
        return;
      }
      if (props.field === POLICY_TRANSLATION) {
        workspaceController.combineRulesByAdoptionDecision();
      }
      let policyState = workspaceController.getPolicyState(
        healthPlan.healthPlanDisplayName,
        props.field,
        props.row[props.field].status,
        props.row["coding"].status,
        props.row["translation"].status,
        userContext.groups[0],
        userContext.POETSkill
      );
      policyState.policyKey = props.id;
      policyState.policyWorkflowTaskCategory = props.field;
      policyState.policyWorkflowTaskStatus = props.row[props.field].status;
      policyState.healthPlanDefaultId = healthPlan.healthPlanDefaultId;
      policyState.healthPlanName = healthPlan.healthPlanName;
      policyState.healthPlanKey = healthPlan.healthPlanKey;
      policyState.codeGroups = props.row.codeGroups;
      policyState.cowName =
        props.row?.bulkActionWorkflowRefDTO?.categoryOfWorkName;
      if (props.row?.quarter) {
        policyState.quarter = getQuarterIdentifier(
          props.row?.quarter,
          props.row?.bulkActionWorkflowRefDTO
        );
      } else {
        policyState.quarter = null;
      }

      if (policyState.screenRoute === "/policy-rules") {
        workspaceController.setClob(
          convertDxLimitToCantHave(workspaceController.clob)
        );
      }

      setWorkspaceController(workspaceController);
      //TEMPORARY: using screenRoute from policyState until Orchestrator is available
      sessionStorage.setItem("policyState", JSON.stringify(policyState));
      setIsLoading(false); // End loading
      //  why are we setting session when we are passing in state below?
      navigate(policyState.screenRoute, {
        state: {
          policyNumber: props.row.number,
          policyTitle: props.row.title,
          policyVersion: props.row.version,
          policyState,
        },
      });
    },
    [navigate, setWorkspaceController, userContext, convertDxLimitToCantHave]
  );

  const fetchData = useCallback(async () => {
    try {
      let lobArray = [];
      setIsLoading(true);

      const queryParams = {
        healthPlanKey: selectedHealthPlan.healthPlanKey,
        healthPlanName: selectedHealthPlan.healthPlanName,
        lineOfBusinessKeys: lobArray,
      };

      const response =
        await api.policyEnforcementGrid.getActivePolicyGrid(queryParams);

      if (response.status === 200) {
        const data = normalizePolicyEnforcementRows(response.data);

        if (Array.isArray(data)) {
          setAllData(data);
        } else {
          console.error(
            "Data returned does not match the required format:",
            data
          );
        }
      }
    } catch (e) {
      dispatch(
        setDialog({
          message: formErrorStrfromObj(e),
          title: "Error",
        })
      );
    } finally {
      setIsLoading(false);
    }
  }, [selectedHealthPlan]);
  useEffect(() => {
    if (!selectedHealthPlan) return undefined;
    const timeoutId = setTimeout(() => {
      if (shouldReloadPolicyData) {
        dispatch(reloadPolicyData(false));
      }
      fetchData();
    }, 0);
    return () => clearTimeout(timeoutId);
  }, [shouldReloadPolicyData, selectedHealthPlan, fetchData, dispatch]);

  const displayData = useMemo(() => {
    if (!userContext) return [];
    if (!Array.isArray(allData)) return allData;
    const userEmail = userContext.preferred_username;
    const latestVersionPolicyKeySet = buildLatestVersionPolicyKeySet(allData);

    return allData.filter((row) => {
      let keepRow = matchesStatusFilter(
        row,
        selectedStatus,
        latestVersionPolicyKeySet
      );

      if (!keepRow) {
        return false;
      }

      if (selectedAssignee === "Assigned to me") {
        const isAssignedToCurrentUser =
          row.configuration?.assignedUserEmail === userEmail ||
          row.coding?.assignedUserEmail === userEmail ||
          row.translation?.assignedUserEmail === userEmail ||
          row.pdccab?.assignedUserEmail === userEmail;
        if (!isAssignedToCurrentUser) {
          keepRow = false;
        }
      }

      if (selectedAssignee === "Unassigned") {
        const allAssigned = [
          row.configuration?.assignedUserEmail,
          row.coding?.assignedUserEmail,
          row.translation?.assignedUserEmail,
        ].every(Boolean);
        if (allAssigned) {
          keepRow = false;
        }
      }

      return keepRow;
    });
  }, [allData, selectedStatus, selectedAssignee]);

  const restrictedParentPolicyKeys = useMemo(() => {
    if (!Array.isArray(allData)) return new Set();

    return allData.reduce((parentKeys, row) => {
      const normalizedCategory = getNormalizedCategoryOfWork(row);

      if (
        row?.parentPolicyKey != null &&
        RESTRICTED_CHILD_ACTION_TYPES.has(normalizedCategory)
      ) {
        parentKeys.add(row.parentPolicyKey);
      }

      return parentKeys;
    }, new Set());
  }, [allData]);

  const rowData = useMemo(
    () =>
      displayData.map((row) => {
        const quarterReturnValue = getQuarterDisplayValue({
          quarter: row.quarter,
          quarterDisplay: row.quarterDisplay,
          year: row.year ? String(row.year).substring(2) : row.year,
          yearDisplay: row.yearDisplay,
          typeOfChange: row.typeOfChange,
          typeOfChangeName: row.typeOfChangeName,
          typeOfChangeText: row.typeOfChangeText,
          bulkActionWorkflowRefDTO: row.bulkActionWorkflowRefDTO,
        });

        // Handle EED
        const eed = row.enforcementDatesAndEarliearEnforcementDates
          ? new Date("").toLocaleDateString("en-US")
          : "";

        // handle PDCCAB
        const pdccab = row.pdccab
          ? {
              ...(row.pdccab.status ? { status: row.pdccab.status } : {}),
              ...(row.pdccab.assignedUserName
                ? { assignedUser: row.pdccab.assignedUserName }
                : {}),
            }
          : {};

        // handle coding
        const coding = row.coding
          ? {
              ...(row.coding.status ? { status: row.coding.status } : {}),
              ...(row.coding.reviewerUserName
                ? { reviewerUser: row.coding.reviewerUserName }
                : {}),
              ...(row.coding.assignedUserName
                ? { assignedUser: row.coding.assignedUserName }
                : {}),
            }
          : {};

        // handle translation
        const translation = row.translation
          ? {
              ...(row.translation.status
                ? { status: row.translation.status }
                : {}),
              ...(row.translation.reviewerUserName
                ? { reviewerUser: row.translation.reviewerUserName }
                : {}),
              ...(row.translation.assignedUserName
                ? { assignedUser: row.translation.assignedUserName }
                : {}),
            }
          : {};

        // handle configuration
        const configuration = row.configuration
          ? {
              ...(row.configuration.status
                ? { status: row.configuration.status }
                : {}),
              ...(row.configuration.assignedUserName
                ? { assignedUser: row.configuration.assignedUserName }
                : {}),
            }
          : {};

        return {
          policyKey: row.policyKey,
          number: row.policyNumber,
          title: row.policyTitle,
          version: row.versionDisplay,
          quarter: quarterReturnValue,
          eed: eed,
          pdc: pdccab,
          coding: coding,
          translation: translation,
          configuration: configuration,
          release: getLatestReleaseMetadata(row),
          bulkActionWorkflowRefDTO: row.bulkActionWorkflowRefDTO,
          policyChangeDate: row.policyChangeDate,
          earliestEnforcementDate: row.earliestEffectiveDate,
          codeGroups: row.codeGroups,
          parentPolicyKey: row.parentPolicyKey,
        };
      }),
    [displayData]
  );
  useEffect(() => {
    // update selection on rowdata change
    const timeoutId = setTimeout(() => {
      setSelectedPolicies((prev) =>
        prev.filter((policy) =>
          rowData.some((data) => data.policyKey === policy.policyKey)
        )
      );
    }, 0);
    return () => clearTimeout(timeoutId);
  }, [rowData]);

  const renderStatus = useCallback(
    (props) => {
      const { value } = props;
      const [status, assignedUser] = value.split("_");
      const translationStatus = props.row["translation"].status
        .trim()
        .toLowerCase();
      const codingStatus = props.row["coding"].status.trim().toLowerCase();
      const configurationStatus = props.row["configuration"].status;
      const field = props.field.trim().toLowerCase();
      const showPolicyNotReadyDialog = () => {
        dispatch(
          setDialog({
            message:
              "The policy can be worked when Adoption Decisions have been finalized.",
            title: "Policy not ready",
          })
        );
      };

      const onButtonClick = () => {
        if (field === "coding" && codingStatus === "pending decision") {
          showPolicyNotReadyDialog();
          return;
        }
        if (
          field === "translation" &&
          translationStatus === "pending decision"
        ) {
          showPolicyNotReadyDialog();
          return;
        }
        if (
          field === "configuration" &&
          configurationStatus === "Pending Decision"
        ) {
          showPolicyNotReadyDialog();
          return;
        }

        if (
          field === "configuration" &&
          (codingStatus !== "complete" || translationStatus !== "complete")
        ) {
          setOpenPolicyNotReadyDialog(true);
          return;
        }
        getPolicyRules(selectedHealthPlan, props);
      };

      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Link component="button" onClick={onButtonClick}>
            <Stack direction={"column"}>
              <span>{status}</span>
              <span>{assignedUser}</span>
            </Stack>
          </Link>
        </Box>
      );
    },
    [getPolicyRules, selectedHealthPlan]
  );

  const hasDisplayData = useMemo(
    () => displayData.length > 0,
    [displayData.length]
  );
  const showPolicyKeyColumn = useMemo(
    () => shouldShowPolicyKeyColumn(window?.env?.REACT_APP_ENVIRONMENT),
    []
  );
  const columns = useMemo(
    () => [
      {
        field: "policyKey",
      },
      {
        field: "number",
        headerName: "Policy #",
        flex: 1.25,
        headerAlign: "center",
        align: "center",
      },
      {
        field: "title",
        headerName: "Title",
        flex: 2,
        headerAlign: "center",
      },
      {
        field: "version",
        headerName: "Version",
        flex: 1,
        headerAlign: "center",
        align: "center",
      },
      {
        field: "quarter",
        headerName: "Quarter",
        flex: 1,
        renderCell: (params) => (
          <Box sx={wrappedCellContentSx}>{params.value || ""}</Box>
        ),
        headerAlign: "center",
        align: "center",
      },
      {
        field: "policyChangeDate",
        headerName: "PCD",
        flex: 1,
        headerAlign: "center",
        align: "center",
        renderHeader: (params) => (
          <Tooltip title="Policy Change Date">
            <HeaderTitle>{params.colDef.headerName}</HeaderTitle>
          </Tooltip>
        ),
      },
      {
        field: "earliestEnforcementDate",
        headerName: "EEfd",
        flex: 2,
        headerAlign: "center",
        align: "center",
        renderHeader: (params) => (
          <Tooltip title="Earliest Enforcement Date">
            <HeaderTitle>{params.colDef.headerName}</HeaderTitle>
          </Tooltip>
        ),
      },
      {
        field: PDC_CAB,
        headerName: "PDC/CAB",
        flex: 1.5,
        valueGetter: (value, row) => row.pdc.status,
        renderCell: (params) => params.value || "",
        headerAlign: "center",
        align: "center",
      },
      {
        field: POLICY_CODING,
        headerName: "Coding",
        flex: 1.5,
        valueGetter: (value, row) => {
          const { status, assignedUser, reviewerUser } = row.coding;
          let str = "";
          if (status) str += status;
          str += "_";
          if (
            (status === "Assigned" ||
              status === "In Progress" ||
              status === "Complete") &&
            assignedUser
          ) {
            str += assignedUser;
          } else if (
            (status === "Peer Review" || status === "PDC Review") &&
            reviewerUser
          ) {
            str += reviewerUser;
          }
          return str;
        },
        renderCell: (props) => renderStatus(props),
        headerAlign: "center",
        align: "center",
      },
      {
        field: POLICY_TRANSLATION,
        headerName: "Translation",
        flex: 1.5,
        valueGetter: (value, row) => {
          const { status, assignedUser, reviewerUser } = row.translation;
          let str = "";
          if (status) str += status;
          str += "_";
          if (
            (status === "Assigned" ||
              status === "In Progress" ||
              status === "Complete") &&
            assignedUser
          ) {
            str += assignedUser;
          } else if (
            (status === "Peer Review" || status === "PDC Review") &&
            reviewerUser
          ) {
            str += reviewerUser;
          }
          return str;
        },
        renderCell: (props) => renderStatus(props),
        headerAlign: "center",
        align: "center",
      },
      {
        field: POLICY_CONFIGURATION,
        headerName: "Configuration",
        flex: 1.5,
        valueGetter: (value, row) => {
          const { status, assignedUser } = row.configuration;
          let str = "";
          if (status) str += status;
          str += "_";
          if (assignedUser) str += assignedUser;
          return str;
        },
        renderCell: (props) => renderStatus(props),
        headerAlign: "center",
        align: "center",
      },
      {
        field: POLICY_RELEASE,
        headerName: "Release",
        flex: 1.5,
        valueGetter: (value, row) => getReleaseDisplayValue(row.release),
        renderCell: (params) => (
          <Box sx={releaseCellContentSx}>{params.value || ""}</Box>
        ),
        headerAlign: "center",
        align: "center",
      },
      {
        field: "action",
        headerName: "Action",
        flex: 1.5,
        renderCell: (props) => {
          if (!props?.row) return "";
          const codingComplete =
            props.row.coding.status.trim().toUpperCase() === COMPLETE;
          const translationComplete =
            props.row.translation.status.trim().toUpperCase() === COMPLETE;
          const configurationComplete =
            props.row.configuration.status.trim().toUpperCase() === COMPLETE;
          const breakFix =
            props.row?.bulkActionWorkflowRefDTO?.categoryOfWorkName
              ?.trim()
              .toUpperCase() === BREAKFIX;
          const CAB =
            props.row?.bulkActionWorkflowRefDTO?.categoryOfWorkName
              ?.trim()
              .toUpperCase() === "CAB";

          return (
            <RowAction
              breakFix={breakFix}
              CAB={CAB}
              hasRestrictedChildPolicy={restrictedParentPolicyKeys.has(
                props.row.policyKey
              )}
              codingComplete={codingComplete}
              translationComplete={translationComplete}
              configurationComplete={configurationComplete}
              policyKey={props.row.policyKey}
              policyNumber={props.row.number}
              policyTitle={props.row.title}
              reloadGrid={fetchData}
            />
          );
        },
        headerAlign: "center",
        align: "center",
      },
    ],
    [fetchData, renderStatus, restrictedParentPolicyKeys]
  );

  const getRowById = (row) => row.policyKey;

  const resizeEvent = useCallback(
    () => calculateGridHeight(topPanelRef, setGridHeight),
    [topPanelRef.current, setGridHeight]
  );
  useGridResizeHook(resizeEvent);

  const handleAccordionToggle = () => {
    setTimeout(() => {
      resizeEvent();
    }, 400);
  };
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <UseCaseNotReady
        openPolicyNotReadyDialog={openPolicyNotReadyDialog}
        setOpenPolicyNotReadyDialog={setOpenPolicyNotReadyDialog}
      />
      <Box ref={topPanelRef}>
        <TopPanelComponent pageType={pageType}>
          <Box
            sx={{
              width: 1,
            }}
            display="flex"
            px={(theme) => theme.spacing(1)}
          >
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
              }}
            >
              <Typography
                variant="h2"
                component="h2"
                sx={{
                  width: 225,
                  alignSelf: "center",
                  fontSize: 18,
                  fontWeight: "bold",
                }}
              >
                Health Plan
              </Typography>
              <HealthPlanSelect />
            </Box>
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={postOHR}
                      onChange={(event) => setPostOHR(event.target.checked)}
                    />
                  }
                  label="Post-Ohr UI"
                />
              </FormGroup>
            </Box>
          </Box>
          <Box
            sx={{
              paddingY: 1,
            }}
          >
            <QuickFilters
              selectedStatus={selectedStatus}
              selectedAssignee={selectedAssignee}
              handleAccordionToggle={handleAccordionToggle}
              setSelectedStatus={setSelectedStatus}
              setSelectedAssignee={setSelectedAssignee}
              onFilterChange={updateSearchParam}
              resetFilter={resetFilter}
            />
          </Box>
        </TopPanelComponent>
      </Box>
      <Box>
        <>
          <Backdrop
            open={isLoading}
            style={{
              zIndex: 1000,
              color: "#fff",
            }}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
          <StyledDataGrid
            sx={{
              maxHeight: `${gridHeight}px !important `,
              "& .MuiDataGrid-cell": {
                display: "flex",
                alignItems: "center",
              },
            }}
            getRowId={getRowById}
            columns={columns}
            columnVisibilityModel={{ policyKey: showPolicyKeyColumn }}
            rows={rowData}
            pageSize={5}
            checkboxSelection
            disableDensitySelector
            disableColumnSelector
            disableColumnFilter
            //rowHeight={"auto"}
            getRowHeight={() => "auto"}
            slots={{
              toolbar: CustomToolBar,
            }}
            slotProps={{
              toolbar: {
                selectedHealthPlan,
                displayData,
                allPolicies: rowData,
                fetchData,
                hasDisplayData,
                selectedPolicies,
                showBulkUpdate,
              },
            }}
            onRowSelectionModelChange={onRowSelectionModelChange}
          />
        </>
      </Box>
    </Box>
  );
}

export default PolicyEnforcementRules;
